import { useSiteLocale } from '../../hooks/useLocalizedNavigate';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useFinder } from './useFinder';
import { CountryOptionType, OptionType } from '../../utils/types';
import EventRow from '../common/EventRow/EventRow';
import { setEventType } from '../../store/eventSearchFilters/eventSearchFilters';
import { eventTypeOptions } from '../../utils/consts';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import {
    Button,
    CountrySelect,
    GenericError,
    Icon,
    PageLoader,
    TextField,
    Title,
} from '../common';
import Autocomplete from '../common/Autocomplete/Autocomplete';
import { Link, useSearchParams } from 'react-router-dom';
import useFinderSeo from './useFinderSeo';

import './finder.css';

const Finder = () => {
    const [searchParams] = useSearchParams();
    const auth = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const geoData = useAppSelector(state => state.geoData);
    const { t } = useTranslation();
    const locale = useSiteLocale();
    const eventSearchFilters = useAppSelector(
        state => state.eventSearchFilters
    );

    const eventType = eventSearchFilters.eventType || '';
    const initialCountryCode =
        searchParams.get('country') || auth.authData?.country.code || 'GB';
    const initialCity =
        searchParams.get('city') || auth.authData?.city.name || '';

    const [countryCode, setCountryCode] = useState(initialCountryCode);
    const [selectedCity, setSelectedCity] = useState(initialCity);
    const [appliedFilters, setAppliedFilters] = useState({
        countryCode: initialCountryCode,
        city: initialCity,
    });

    useEffect(() => {
        setCountryCode(initialCountryCode);
        setSelectedCity(initialCity);
        setAppliedFilters({
            countryCode: initialCountryCode,
            city: initialCity,
        });
    }, [initialCountryCode, initialCity]);

    const {
        isLoading,
        error,
        showLoadMoreButton,
        onShowMoreClick,
        moreIsLoading,
        getCitiesHandler,
        isGeoDataLoading,
        citiesData,
        groupedEvents,
        events,
        disableSportFilters,
    } = useFinder(
        appliedFilters.countryCode,
        eventType,
        appliedFilters.city,
        countryCode
    );

    useFinderSeo({ appliedCity: appliedFilters.city, groupedEvents });

    const generateOptions = useMemo(() => {
        const translatedOptions = eventTypeOptions.map(option => ({
            id: option.id,
            label: t(`eventTypes.${option.label.toLowerCase()}`),
        }));
        const eventTypes: OptionType[] = [
            { id: 'ALL', label: t('eventTypes.showAll') },
            ...translatedOptions,
        ];
        const citiesOptions: OptionType[] = [];

        citiesData?.forEach(city => {
            citiesOptions.push({
                id: city.id,
                label: city.name,
            });
        });
        return { eventTypes, citiesOptions };
    }, [citiesData, t]);

    const onCountryChange = (_: string, option: CountryOptionType) => {
        if (option.id !== countryCode) {
            setSelectedCity('');
            setCountryCode(option.id);
        }
    };

    const onCityChange = (_: string, result: OptionType) => {
        setSelectedCity(result.label);
    };

    const onEventTypeChange = (eventTypeId: string) => {
        dispatch(setEventType(eventTypeId === 'ALL' ? null : eventTypeId));
    };

    const onSearchClick = () => {
        if (!selectedCity) {
            return;
        }
        setAppliedFilters({
            countryCode,
            city: selectedCity,
        });
    };

    if (error || geoData.error) {
        return <GenericError error={error} />;
    }

    const selectedEventType = eventSearchFilters.eventType || 'ALL';

    return (
        <div className="finder page-container">
            <div className="finder-title">
                <h1>
                    {t(appliedFilters.city ? 'finder.resultsTitle' : 'finder.title')}
                    {appliedFilters.city && <> <span>{appliedFilters.city}</span></>}
                </h1>
                <p>{t('finder.subtitle')}</p>
            </div>
            <section className="finder-filters" aria-label={t('finder.searchLabel')}>
                <p className="finder-filter-label" id="finder-location-label">{t('finder.location')}</p>
                <div className="finder-filters-inputs" role="group" aria-labelledby="finder-location-label">
                    <div>
                        <CountrySelect
                            name="countryCode"
                            value={countryCode}
                            onSelect={onCountryChange}
                        />
                        <Autocomplete
                            name="city"
                            startSearch={getCitiesHandler}
                            isLoading={isGeoDataLoading}
                            error={geoData.error}
                            results={generateOptions.citiesOptions}
                            handleOptionClick={onCityChange}
                            placeholder={t('common.cityPlaceholder')}
                            initialValue={selectedCity}
                            iconName="bx-map"
                            className="finder-autocomplete"
                        />
                    </div>
                    <Button
                        text={t('finder.searchLabel')}
                        variant="green"
                        size="md"
                        className="finder-search-button"
                        disabled={!selectedCity}
                        onClick={onSearchClick}
                    />
                </div>

                <div className="finder-sport-filter">
                    <p className="finder-filter-label" id="finder-sport-label">{t('finder.sport')}</p>
                    <div className="finder-event-types" role="group" aria-labelledby="finder-sport-label">
                        {generateOptions.eventTypes.map(option => (
                            <Button
                                key={option.id}
                                text={option.label}
                                variant={
                                    selectedEventType === option.id
                                        ? 'green'
                                        : 'white'
                                }
                                size="sm"
                                className="finder-sport-pill"
                                disabled={
                                    option.id !== 'ALL' && disableSportFilters
                                }
                                onClick={() =>
                                    onEventTypeChange(option.id as string)
                                }
                            />
                        ))}
                    </div>
                </div>
            </section>
            <div className="finder-event-list" aria-live="polite">
                {!appliedFilters.city ? (
                    <div className="finder-initial-state">
                        <span className="finder-initial-icon" aria-hidden="true">
                            <Icon name="bx-map" size="30px" />
                        </span>
                        <h2>{t('finder.initialTitle')}</h2>
                        <p>{t('finder.initialDescription')}</p>
                    </div>
                ) : !isLoading && events !== null ? (
                    groupedEvents?.length === 0 ? (
                        <div className="finder-event-list-empty">
                            <Icon name="bx-stadium" size="50px" />
                            <Title
                                text={t('finder.noEventsTitle')}
                                size="xl"
                            />
                            <TextField color="subtitle">
                                {t('finder.noEventsSub')}
                            </TextField>
                            <Link to={`/${locale}/create`}>
                                <Button
                                    text={t('createEvent.createEvent')}
                                    variant="green"
                                    size="md"
                                />
                            </Link>
                        </div>
                    ) : (
                        groupedEvents?.map(groupedEvent => (
                            <div
                                key={groupedEvent.key}
                                className="finder-event-list-section"
                            >
                                <Title text={groupedEvent.label} size="l" />
                                {groupedEvent.events.map(event => (
                                    <EventRow
                                        key={event.id}
                                        eventRecord={event}
                                    />
                                ))}
                            </div>
                        ))
                    )
                ) : (
                    <PageLoader />
                )}
                {appliedFilters.city && moreIsLoading && <PageLoader />}
                {appliedFilters.city && showLoadMoreButton && (
                    <div className="finder-event-list-btn">
                        <Button
                            text="Show more records"
                            variant="green"
                            size="md"
                            onClick={onShowMoreClick}
                            disabled={moreIsLoading}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Finder;
